import { TableRow, TableCell, Box, Collapse } from '@mui/material';
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { GridProps } from '@sm-dashboard/types';
import { getRowStyles, separateActionsByLevel, separateColumnsByDisplayLevel } from '../helpFunctions';
import { actionsRender } from '../actions/Actions';
import { renderCell } from './renderCell';
import { Checkbox } from '@sm-dashboard/ui-components';
import AutoExpandedView from '../expanded/AutoExpandedView';
import { ExpansionToggle } from '../expanded/ExpansionToggle';
import { alpha } from '@mui/material/styles';

const isNewRow = (row: unknown): boolean => Boolean((row as any)?.isNew);

const isTextLikeColumn = (column: GridProps<any, any>['columns'][number]): boolean => {
  const type = column.type;
  return (
    type === undefined ||
    type === 'text' ||
    type === 'number' ||
    type === 'date' ||
    type === 'dateTime' ||
    type === 'phone' ||
    type === 'email' ||
    type === 'doc' ||
    type === 'richText'
  );
};

interface DesktopGridRowProps<T, U> {
  row: T;
  index: number;
  columns: GridProps<T, U>['columns'];
  actions: GridProps<T, U>['actions'];
  columnWidths: Array<{ width?: number; maxWidth?: number }>;
  /** Whether the grid reserves the expansion column (layout-level, true if any row can expand). */
  expandable: boolean;
  /** Whether THIS row can actually expand (toggle shown, row clickable). Defaults to `expandable`. */
  rowExpandable?: boolean;
  onExpand?: (row: T) => void;
  onRowClick?: (row: T) => void;
  isExpanded: number | null;
  setIsExpanded: (expanded: number | null) => void;
  selectable?: boolean;
  selectedRows: number[];
  handleSelection: (index: number) => void;
  components?: GridProps<T, U>['components'];
  firstColumnRef: React.RefObject<HTMLDivElement>;
  /** Virtualizer measure ref attached to the main row when windowing is enabled. */
  measureRef?: (el: HTMLElement | null) => void;
  alignColumnsCellSxProps: any;
  style?: GridProps<T, U>['style'];
  editable?: boolean;
  editingCell: { rowIndex: number; columnIndex: number } | null;
  setEditingCell: (cell: { rowIndex: number; columnIndex: number } | null) => void;
  editedValue: string;
  setEditedValue: (value: string) => void;
  editActionsHandler?: (rowIndex: number, field: keyof T, value: string) => void;
  editInputRender?: GridProps<T, U>['editInputRender'];
  // Expanded content props
  childRowsKey?: keyof T;
  renderExpanded?: (row: T, childRow: U | undefined, childIndex: number, mobileView: boolean) => React.ReactNode;
  desktopSideBottomColumns?: boolean;
  panelLayout?: boolean;
}

const DesktopGridRow = <T, U>({
  row,
  index,
  columns,
  actions,
  columnWidths,
  expandable,
  rowExpandable = expandable,
  onExpand,
  onRowClick,
  isExpanded: expandedIndex,
  setIsExpanded,
  selectable,
  selectedRows,
  handleSelection,
  components,
  firstColumnRef,
  measureRef,
  alignColumnsCellSxProps,
  style,
  editable,
  editingCell,
  setEditingCell,
  editedValue,
  setEditedValue,
  editActionsHandler,
  editInputRender,
  childRowsKey,
  renderExpanded,
  desktopSideBottomColumns,
  panelLayout
}: DesktopGridRowProps<T, U>) => {
  const ROW_BORDER = (theme: any) => `2px solid ${alpha(theme.palette.text.primary, 0.05)}`;
  const separatedColumns = useMemo(() => separateColumnsByDisplayLevel(columns, false), [columns]);
  const primaryColumns = useMemo(() => separatedColumns['primary'], [separatedColumns]);
  const [primaryActions, regularActions] = useMemo(() => separateActionsByLevel(actions || [], false), [actions]);
  const rowIsNew = useMemo(() => isNewRow(row), [row]);

  const handleRowClick = (event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('.MuiCheckbox-root')) return;
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    if (!rowExpandable) return;
    const newExpandedState = expandedIndex === index ? null : index;
    setIsExpanded(newExpandedState);
    onExpand?.(row);
  };

  const isExpanded = useMemo(() => expandedIndex === index, [expandedIndex, index]);

  const expandedChildRows = useMemo(
    () => (rowExpandable ? (childRowsKey && (row[childRowsKey] as U[])) || ([row] as T[]) : []),
    [rowExpandable, childRowsKey, row]
  );

  /** Keep main row bottom border off while expanded and until Collapse finishes exiting (expanded tr has no borders). */
  const [collapseExiting, setCollapseExiting] = useState(false);
  const prevExpandedRef = useRef(isExpanded);

  useLayoutEffect(() => {
    if (prevExpandedRef.current && !isExpanded) {
      setCollapseExiting(true);
    } else if (isExpanded) {
      setCollapseExiting(false);
    }
    prevExpandedRef.current = isExpanded;
  }, [isExpanded]);

  const showMainRowBottomBorder = !(rowExpandable && (isExpanded || collapseExiting));

  return (
    <Fragment>
      <TableRow
        hover={false}
        ref={measureRef}
        data-index={index}
        sx={{
          height: `${style?.rowHeight ?? 40}px`,
          ...getRowStyles(row, {}, style),
          cursor: rowExpandable || onRowClick ? 'pointer' : 'default',
          '&:hover': {
            backgroundColor: 'transparent'
          },
          '& .MuiTableCell-root': {
            height: `${style?.rowHeight ?? 40}px`,
            py: 0,
            borderBottom: theme => (showMainRowBottomBorder ? ROW_BORDER(theme) : 'none !important')
          }
        }}
        onClick={handleRowClick}
      >
        {/* Selection Checkbox */}
        {selectable && (
          <TableCell
            ref={firstColumnRef}
            padding="checkbox"
            sx={{
              width: 35,
              minWidth: 35,
              maxWidth: 35,
              py: 0,
              px: 0.5
            }}
          >
            <Checkbox
              size="small"
              checked={selectedRows?.includes(index) ?? false}
              onChange={() => handleSelection(index)}
            />
          </TableCell>
        )}

        {/* row icon */}
        {components?.rowIcon && <TableCell sx={{ width: '30px' }}>{components.rowIcon(row)}</TableCell>}

        {/* Empty Cell for Add Button layout */}
        {components?.AddButton && <TableCell ref={firstColumnRef} />}

        {/* Data Cells */}
        {primaryColumns.map((column, columnIndex) => (
          <TableCell
            ref={!selectable && !components?.AddButton && columnIndex === 0 ? firstColumnRef : undefined}
            sx={{
              backgroundColor: column.color,
              ...columnWidths[columnIndex],
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: column?.overflow || 'hidden',
              ...(rowIsNew && isTextLikeColumn(column)
                ? {
                    fontWeight: 600,
                    '& .MuiTypography-root': { fontWeight: 600 },
                    '& a': { fontWeight: 600 }
                  }
                : {})
            }}
            key={columnIndex}
            onDoubleClick={() => {
              if (editable) {
                setEditingCell({ rowIndex: index, columnIndex });
                setEditedValue(row[column.field as keyof typeof row] as string);
              }
            }}
          >
            {editingCell?.rowIndex === index && editingCell?.columnIndex === columnIndex && editable ? (
              editInputRender ? (
                editInputRender(row, column, () => {
                  setEditingCell(null);
                })
              ) : !column?.renderCell && !column?.valueGetter ? (
                <Box
                  component="input"
                  sx={{
                    width: '100%',
                    border: 'none',
                    padding: '4px',
                    '&:focus': {
                      outline: 'none',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  autoFocus
                  value={editedValue}
                  onChange={e => setEditedValue(e.target.value)}
                  onBlur={() => {
                    setEditingCell(null);
                    editActionsHandler?.(index, column.field as keyof T, editedValue);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setEditingCell(null);
                      setEditedValue('');
                    }
                  }}
                />
              ) : (
                renderCell(row, column, columnWidths[columnIndex].maxWidth)
              )
            ) : (
              renderCell(row, column, columnWidths[columnIndex].maxWidth)
            )}
          </TableCell>
        ))}

        {/* Cell for align all visibleColumns to the start */}
        <TableCell sx={{ ...alignColumnsCellSxProps }} />

        {/* Primary Actions */}
        {primaryActions && primaryActions.length > 0 && (
          <TableCell
            colSpan={primaryActions.length}
            sx={{
              width: 1,
              whiteSpace: 'nowrap',
              paddingLeft: 0,
              paddingRight: 0,
              // Last cell of the row (no expansion toggle after it) — keep the buttons off the edge.
              ...(!expandable ? { paddingInlineEnd: 2 } : {}),
              maxWidth: '1%'
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              {actionsRender(primaryActions, row)}
            </Box>
          </TableCell>
        )}

        {/* Auto Expansion Toggle — cell kept reserved for column alignment even when this row can't expand */}
        {expandable && (
          <TableCell width={'40px'} sx={{ padding: '0 9px 0 16px !important' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
              {rowExpandable && <ExpansionToggle isExpanded={isExpanded} />}
            </Box>
          </TableCell>
        )}
      </TableRow>

      {/* Expanded Content */}
      {rowExpandable && (
        <>
          {expandedChildRows.map((childRow, childIndex) =>
            // Add bottom spacing after the *whole* expanded block (only once, on last child row)
            // so it feels separated from the next data row.
            // (Spacing requested: 10px)
            // Note: we keep borders off on the expanded <tr>.
            (() => {
              const isLastExpandedRow = childIndex === expandedChildRows.length - 1;
              return (
                <TableRow
                  key={childIndex}
                  sx={{
                    '& .MuiTableCell-root': {
                      borderTop: 'none !important',
                      borderBottom: 'none !important'
                    }
                  }}
                >
                  <TableCell
                    sx={{
                      borderTop: 'none !important',
                      borderBottom: 'none !important',
                      paddingBottom: isExpanded && isLastExpandedRow ? '10px' : 0,
                      paddingTop: isExpanded && childIndex === 0 ? '5px' : 0,
                      paddingLeft: 0,
                      paddingRight: 0,
                      verticalAlign: 'top'
                    }}
                    colSpan={12}
                  >
                    <Collapse
                      in={isExpanded}
                      timeout="auto"
                      unmountOnExit
                      onExited={() => {
                        if (isLastExpandedRow) {
                          setCollapseExiting(false);
                        }
                      }}
                      sx={{ borderBottom: 'unset', width: '100%', display: 'block' }}
                    >
                      {renderExpanded ? (
                        <Box
                          sx={theme => ({
                            width: '100%',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            bgcolor: theme.palette.grey[600],
                            py: 3,
                            px: 2
                          })}
                        >
                          {renderExpanded(row, childRowsKey ? (childRow as U) : undefined, childIndex, false)}
                        </Box>
                      ) : (
                        <AutoExpandedView
                          row={row}
                          columns={columns}
                          actions={regularActions}
                          firstColumnRef={firstColumnRef}
                          childRow={childRow}
                          childIndex={childIndex}
                          desktopSideBottomColumns={desktopSideBottomColumns}
                          panelLayout={panelLayout}
                        />
                      )}
                    </Collapse>
                  </TableCell>
                </TableRow>
              );
            })()
          )}
        </>
      )}
    </Fragment>
  );
};

export default DesktopGridRow;
